import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Building2, Check, ExternalLink, Plus, RefreshCw } from 'lucide-react';
import AssistantCompanyProfileResource, {
	PageCompanyExtract,
	PageCompanyContact,
	PageCompanyOverrides
} from 'Resources/AssistantCompanyProfile';
import { openCrmEntity } from 'App/shadcn/pages/Assistant/openCrmEntity';
import type { PageContext } from 'App/shadcn/pages/AIWorkspace/Chat/buildPageContextString';
import AddToUpsalesModal from './AddToUpsalesModal';
import LinkedInCompanyPeople from './LinkedInCompanyPeople';
import { matchablePageDomain, isSameWebsite } from './pageCompanyDomain';
import T from 'Components/Helpers/translate';
import { accountManagerMeta } from './accountManagerMeta';

type Props = {
	pageContext: PageContext;
	// Per-URL extraction cache owned by EmbedAIChat so it survives tab switches.
	extractCache: Map<string, PageCompanyExtract>;
	// Lets EmbedAIChat flip the tab label to "existing" after a create.
	onCompanyAdded: (clientId: number, clientName: string) => void;
};

type Phase = 'loading' | 'error' | 'ready';
type CompanyForm = { name: string; orgNumber: string; phone: string; street: string; zip: string; city: string };
type ContactForm = { name: string; role: string; company: string; email: string; phone: string };
// What is currently saving: the company row or a contact row (by index).
type SavingKey = 'company' | number | null;

const buildCompanyForm = (extract: PageCompanyExtract | null): CompanyForm => ({
	name: extract?.prospecting?.name ?? extract?.company?.name ?? '',
	orgNumber: extract?.prospecting?.orgNumber ?? extract?.company?.orgNumber ?? '',
	phone: extract?.company?.phone ?? '',
	street: extract?.company?.street ?? '',
	zip: extract?.company?.zip ?? '',
	city: extract?.company?.city ?? extract?.prospecting?.city ?? ''
});

// Whether a person works at the page's company. The server tags this
// (`belongsToPage`); default to "ours" when the tag is absent.
const isPageContact = (contact: PageCompanyContact): boolean => contact.belongsToPage !== false;

const AddToUpsalesTab: React.FC<Props> = ({ pageContext, extractCache, onCompanyAdded }) => {
	const [phase, setPhase] = useState<Phase>('loading');
	const [extract, setExtract] = useState<PageCompanyExtract | null>(null);
	const [companyForm, setCompanyForm] = useState<CompanyForm>(buildCompanyForm(null));
	const [contactForm, setContactForm] = useState<ContactForm | null>(null);
	const [modal, setModal] = useState<'company' | number | null>(null);
	const [saving, setSaving] = useState<SavingKey>(null);
	const [saveError, setSaveError] = useState(false);
	// Prefill snapshot — only fields the user changed are sent as overrides, so
	// untouched fields still get prospecting enrichment server-side.
	const prefill = useRef<CompanyForm | null>(null);
	// Always reflects the current URL (unlike the value captured in a request
	// closure), so a late extract response can tell if the tab has moved on.
	const latestUrl = useRef(pageContext.url);
	latestUrl.current = pageContext.url;
	// URL whose results are currently on screen. Lets us tell same-site navigation
	// (keep the results, refresh in the background) from a different website (show
	// the loader). Null when nothing is displayed yet.
	const shownUrl = useRef<string | null>(null);

	const applyExtract = useCallback((result: PageCompanyExtract, url: string) => {
		setExtract(result);
		shownUrl.current = url;
		const initial = buildCompanyForm(result);
		prefill.current = initial;
		setCompanyForm(initial);
		setPhase('ready');
	}, []);

	const runExtract = useCallback(() => {
		// Internal pages (the Upsales app itself, localhost, search engines, webmail)
		// have no company to extract — opening a created account in a new tab makes
		// the side panel follow it, and extracting the CRM page would just error.
		if (!matchablePageDomain(pageContext.url)) {
			setExtract(null);
			shownUrl.current = null;
			setPhase('ready');
			return;
		}
		// Guard against the browser tab changing mid-flight: a late response for a
		// previous URL must not paint over the current page's results.
		const reqUrl = pageContext.url;
		const cached = extractCache.get(reqUrl);
		if (cached) {
			applyExtract(cached, reqUrl);
			return;
		}
		// Navigating around the same website (LinkedIn profiles, sub-pages): keep the
		// current company + contacts on screen and refresh in the background. Only the
		// first load or a switch to a different website shows the full loader.
		const keepShowing = shownUrl.current !== null && isSameWebsite(shownUrl.current, reqUrl);
		if (!keepShowing) setPhase('loading');
		AssistantCompanyProfileResource.pageCompanyExtract({
			url: pageContext.url,
			title: pageContext.title,
			text: pageContext.text
		})
			.then(result => {
				extractCache.set(reqUrl, result);
				if (reqUrl !== latestUrl.current) return;
				applyExtract(result, reqUrl);
			})
			.catch(() => {
				if (reqUrl !== latestUrl.current) return;
				// A failed background refresh keeps the current results on screen; only a
				// first/website-change load falls back to the error state.
				if (!keepShowing) setPhase('error');
			});
	}, [pageContext.url, pageContext.title, pageContext.text, extractCache, applyExtract]);

	useEffect(() => {
		setModal(null);
		setSaveError(false);
		runExtract();
	}, [runExtract]);

	const dirtyOverrides = (): PageCompanyOverrides | undefined => {
		const overrides: PageCompanyOverrides = {};
		(Object.keys(companyForm) as (keyof CompanyForm)[]).forEach(key => {
			const value = companyForm[key].trim();
			if (value && value !== (prefill.current?.[key] ?? '').trim()) overrides[key] = value;
		});
		return Object.keys(overrides).length ? overrides : undefined;
	};

	// Single add path. `otherCompany` set → add the contact to its own employer
	// (resolved by name server-side); otherwise add to the page's company
	// (created if it doesn't exist yet).
	const submitAdd = (
		key: Exclude<SavingKey, null>,
		contact: PageCompanyContact | null,
		opts: { otherCompany?: string; withOverrides?: boolean }
	) => {
		if (!extract) return;
		setSaving(key);
		setSaveError(false);
		const payload = opts.otherCompany
			? { name: opts.otherCompany, contacts: contact ? [contact] : [] }
			: {
					clientId: extract.crm?.exists ? extract.crm.clientId : undefined,
					prospectingId: extract.prospecting?.prospectingId,
					name: companyForm.name.trim() || extract.prospecting?.name || extract.company?.name,
					domain: extract.company?.domain ?? extract.prospecting?.domain,
					orgNumber: extract.prospecting?.orgNumber ?? extract.company?.orgNumber ?? undefined,
					// A company created from its LinkedIn page is saved with that page (WEB-15613).
					pageUrl: pageContext.url,
					overrides: opts.withOverrides && !extract.crm?.exists ? dirtyOverrides() : undefined,
					contacts: contact ? [contact] : []
			  };
		AssistantCompanyProfileResource.pageCompanyAdd(payload)
			.then(result => {
				const markContact = (existing: PageCompanyContact, index: number): PageCompanyContact =>
					key === index ? { ...existing, ...contact, existsInCrm: true } : existing;
				// Adding someone to their OWN company must not flip the page company
				// to "exists" — only a page-company add does that.
				const updated: PageCompanyExtract = opts.otherCompany
					? { ...extract, contacts: extract.contacts.map(markContact) }
					: {
							...extract,
							crm: { exists: true, clientId: result.clientId, clientName: result.clientName },
							contacts: extract.contacts.map(markContact)
					  };
				extractCache.set(pageContext.url, updated);
				applyExtract(updated, pageContext.url);
				if (!opts.otherCompany) onCompanyAdded(result.clientId, result.clientName);
				setSaving(null);
				setModal(null);
				setContactForm(null);
			})
			.catch(() => {
				setSaving(null);
				setSaveError(true);
			});
	};

	const quickAddContact = (index: number, contact: PageCompanyContact) => {
		if (saving) return;
		if (isPageContact(contact)) {
			submitAdd(index, contact, {});
		} else {
			submitAdd(index, contact, { otherCompany: contact.company });
		}
	};

	const openContactModal = (index: number) => {
		const contact = extract?.contacts[index];
		if (!contact || contact.existsInCrm) return;
		setContactForm({
			name: contact.name,
			role: contact.role ?? '',
			company: contact.company ?? '',
			email: contact.email ?? '',
			phone: contact.phone ?? ''
		});
		setModal(index);
	};

	const submitContactModal = (index: number) => {
		const original = extract?.contacts[index];
		if (!original || !contactForm || !contactForm.name.trim()) return;
		const edited: PageCompanyContact = {
			...original,
			name: contactForm.name.trim(),
			role: contactForm.role.trim() || undefined,
			company: contactForm.company.trim() || undefined,
			email: contactForm.email.trim() || undefined,
			phone: contactForm.phone.trim() || undefined
		};
		if (isPageContact(original)) {
			submitAdd(index, edited, {});
		} else {
			submitAdd(index, edited, { otherCompany: contactForm.company.trim() || original.company });
		}
	};

	if (phase === 'loading') {
		return <div className="embed-add embed-add-status">{T('assistant.embedAddAnalyzing')}</div>;
	}
	if (phase === 'error') {
		return (
			<div className="embed-add embed-add-status">
				<div>{T('assistant.embedAddError')}</div>
				<button type="button" className="embed-add-btn" onClick={runExtract}>
					<RefreshCw />
					{T('assistant.embedAddRetry')}
				</button>
			</div>
		);
	}
	if (!extract || (!extract.company && !extract.prospecting)) {
		return <div className="embed-add embed-add-status">{T('assistant.embedAddNoCompany')}</div>;
	}

	const existsInCrm = Boolean(extract.crm?.exists);
	const clientId = extract.crm?.clientId;
	const companyName =
		extract.crm?.clientName ??
		(companyForm.name.trim() || extract.prospecting?.name || extract.company?.name || '');
	const companyMeta = [
		extract.prospecting?.city,
		extract.prospecting?.orgNumber,
		accountManagerMeta(extract.crm?.accountManagers)
	]
		.filter(Boolean)
		.join(' · ');

	const ourPeople: { contact: PageCompanyContact; index: number }[] = [];
	const otherPeople: { contact: PageCompanyContact; index: number }[] = [];
	extract.contacts.forEach((contact, index) => {
		(isPageContact(contact) ? ourPeople : otherPeople).push({ contact, index });
	});

	const renderContactRow = (
		{ contact, index }: { contact: PageCompanyContact; index: number },
		showCompany: boolean
	) => {
		const meta = showCompany
			? [contact.role, contact.company].filter(Boolean).join(' · ')
			: [contact.role, contact.email].filter(Boolean).join(' · ');
		return (
			<button
				key={`${contact.name}-${index}`}
				type="button"
				className="embed-add-item"
				disabled={contact.existsInCrm}
				onClick={() => openContactModal(index)}
			>
				<span className="embed-add-item-info">
					<span className="embed-add-item-name">{contact.name}</span>
					{meta ? <span className="embed-add-item-meta">{meta}</span> : null}
				</span>
				{contact.existsInCrm ? (
					<span className="embed-add-item-done" title={T('assistant.embedAddAlreadyContact')}>
						<Check />
					</span>
				) : (
					<span
						role="button"
						className={`embed-add-item-action${saving === index ? ' is-saving' : ''}`}
						title={T('default.addContact')}
						aria-label={T('default.addContact')}
						onClick={e => {
							e.stopPropagation();
							quickAddContact(index, contact);
						}}
					>
						<Plus />
					</span>
				)}
			</button>
		);
	};

	const modalContact = typeof modal === 'number' ? extract.contacts[modal] : null;
	const modalIsOther = Boolean(modalContact) && !isPageContact(modalContact as PageCompanyContact);

	return (
		<div className="embed-add">
			<div className="embed-add-scroll">
				{saveError ? <div className="embed-add-error">{T('assistant.embedAddSaveError')}</div> : null}
				<button
					type="button"
					className="embed-add-item embed-add-item-company"
					onClick={() => setModal('company')}
				>
					<Building2 className="embed-add-item-icon" />
					<span className="embed-add-item-info">
						<span className="embed-add-item-name">{companyName}</span>
						{companyMeta ? <span className="embed-add-item-meta">{companyMeta}</span> : null}
					</span>
					{existsInCrm ? (
						<span className="embed-add-item-done" title={T('assistant.embedAddOpen')}>
							<Check />
							<ExternalLink />
						</span>
					) : (
						<span
							role="button"
							className={`embed-add-item-action${saving === 'company' ? ' is-saving' : ''}`}
							title={T('assistant.embedAddCreate')}
							aria-label={T('assistant.embedAddCreate')}
							onClick={e => {
								e.stopPropagation();
								if (!saving) submitAdd('company', null, {});
							}}
						>
							<Plus />
						</span>
					)}
				</button>
				{ourPeople.length ? (
					<div className="embed-add-contacts">
						<div className="embed-add-contacts-heading">{T('assistant.embedAddContactsHeading')}</div>
						{ourPeople.map(entry => renderContactRow(entry, false))}
					</div>
				) : null}
				<LinkedInCompanyPeople
					pageUrl={pageContext.url}
					domain={extract.company?.domain ?? extract.prospecting?.domain}
				/>
				{otherPeople.length ? (
					<div className="embed-add-contacts">
						<div className="embed-add-contacts-heading">{T('assistant.embedAddOtherPeople')}</div>
						{otherPeople.map(entry => renderContactRow(entry, true))}
					</div>
				) : null}
			</div>
			{modal === 'company' ? (
				<AddToUpsalesModal
					title={existsInCrm ? companyName : T('assistant.embedAddCreate')}
					saving={saving === 'company'}
					actionLabel={existsInCrm ? T('assistant.embedAddOpen') : T('assistant.embedAddCreate')}
					actionDisabled={existsInCrm ? !clientId : !companyForm.name.trim()}
					fieldsDisabled={existsInCrm}
					fields={[
						{ key: 'name', label: T('default.name'), value: companyForm.name },
						{ key: 'orgNumber', label: T('default.orgNo'), value: companyForm.orgNumber, half: true },
						{ key: 'phone', label: T('default.phone'), value: companyForm.phone, half: true },
						{ key: 'street', label: T('address.streetAddress'), value: companyForm.street },
						{ key: 'zip', label: T('address.zip'), value: companyForm.zip, half: true },
						{ key: 'city', label: T('address.city'), value: companyForm.city, half: true }
					]}
					onChange={(key, value) => setCompanyForm(prev => ({ ...prev, [key]: value }))}
					onAction={() => {
						if (existsInCrm && clientId) {
							openCrmEntity('client', clientId);
							setModal(null);
						} else {
							submitAdd('company', null, { withOverrides: true });
						}
					}}
					onClose={() => setModal(null)}
				/>
			) : null}
			{typeof modal === 'number' && contactForm ? (
				<AddToUpsalesModal
					title={extract.contacts[modal]?.name ?? T('default.addContact')}
					saving={saving === modal}
					actionLabel={T('default.addContact')}
					actionDisabled={!contactForm.name.trim() || (modalIsOther && !contactForm.company.trim())}
					fields={[
						{ key: 'name', label: T('default.name'), value: contactForm.name },
						{ key: 'role', label: T('default.title'), value: contactForm.role },
						...(modalIsOther
							? [{ key: 'company', label: T('default.client'), value: contactForm.company }]
							: []),
						{ key: 'email', label: T('default.email'), value: contactForm.email },
						{ key: 'phone', label: T('default.phone'), value: contactForm.phone }
					]}
					onChange={(key, value) => setContactForm(prev => (prev ? { ...prev, [key]: value } : prev))}
					onAction={() => submitContactModal(modal)}
					onClose={() => {
						setModal(null);
						setContactForm(null);
					}}
				/>
			) : null}
		</div>
	);
};

export default AddToUpsalesTab;
